import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import BannerSlot from '../components/dashboard/BannerSlot';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { studentAPI } from '../utils/api';
import GoalSetter from '../components/dashboard/GoalSetter';

const StatCard = ({ icon, value, label, trend, color }) => (
  <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: color || 'var(--brand-primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: '24px', fontWeight: 800, color: 'var(--text)', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: 600, marginTop: '2px' }}>{label}</div>
      {trend && <div style={{ fontSize: '9px', fontWeight: 700, color: trend.up ? 'var(--green)' : 'var(--muted)', marginTop: '2px' }}>{trend.text}</div>}
    </div>
  </div>
);

const RevisionItem = ({ revision, onRevise }) => {
  const dayLabels = { 1: { label: 'DAY 1', cls: 'badge-revision-due' }, 4: { label: 'DAY 4', cls: '' }, 7: { label: 'DAY 7', cls: '' } };
  const isOverdue = revision.status === 'overdue';

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '9px 12px', borderRadius: 'var(--radius-sm)',
      border: isOverdue ? '1px solid var(--red)' : '1px solid var(--border)',
      background: isOverdue ? 'rgba(220,38,38,0.02)' : 'var(--white)',
      cursor: 'pointer',
      transition: 'border-color 0.15s'
    }}
      onClick={() => onRevise(revision.topic_id)}
      onMouseEnter={e => !isOverdue && (e.currentTarget.style.borderColor = 'var(--brand-primary)')}
      onMouseLeave={e => !isOverdue && (e.currentTarget.style.borderColor = 'var(--border)')}
    >
      <span style={{
        fontSize: '9px', fontWeight: 800, padding: '2px 7px', borderRadius: '20px', whiteSpace: 'nowrap', flexShrink: 0,
        background: isOverdue ? 'var(--red-light)' : revision.revision_number === 1 ? 'var(--blue-light)' : revision.revision_number === 4 ? 'var(--yellow-light)' : 'var(--green-light)',
        color: isOverdue ? 'var(--red)' : revision.revision_number === 1 ? 'var(--blue)' : revision.revision_number === 4 ? '#B45309' : 'var(--green)'
      }}>
        {isOverdue ? '⚠ OVERDUE' : `DAY ${revision.revision_number}`}
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{revision.topic_name}</div>
        <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{revision.subject} · {revision.chapter_name}</div>
      </div>

      <span style={{ fontSize: '9px', fontWeight: 700, background: 'var(--brand-secondary-light)', color: 'var(--brand-secondary)', borderRadius: '20px', padding: '2px 7px', flexShrink: 0 }}>
        {revision.subject?.substring(0, 3).toUpperCase()}
      </span>

      <button
        style={{ background: 'var(--brand-primary)', color: 'white', border: 'none', borderRadius: '6px', padding: '4px 10px', fontSize: '10px', fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}
        onClick={(e) => { e.stopPropagation(); onRevise(revision.topic_id); }}
      >
        Revise Now
      </button>
    </div>
  );
};

const DashboardPage = () => {
  const [data, setData] = useState(null);
  const [goalTarget, setGoalTarget] = useState(5);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { error } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    loadDashboard();
    const interval = setInterval(loadDashboard, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await studentAPI.getDashboard();
      setData(res.data.data);
      if (res.data.data?.weeklyGoal?.target) setGoalTarget(res.data.data.weeklyGoal.target);
    } catch {
      // Use mock data for now until backend Phase 2 is built
      setData(getMockData());
    } finally {
      setLoading(false);
    }
  };

  const getMockData = () => ({
    stats: { topicsMastered: 0, revisionsDueToday: 0, avgMasteryScore: 0, currentStreak: 0 },
    revisionsToday: [],
    continueTopic: null,
    weeklyGoal: { target: 5, completed: 0, topics: [] },
    weakTopics: [],
    subjects: [],
    announcements: []
  });

  if (loading) {
    return (
      <AppShell>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <div className="spinner" style={{ width: '32px', height: '32px' }}></div>
        </div>
      </AppShell>
    );
  }

  const { stats, revisionsToday, continueTopic, weeklyGoal, weakTopics, subjects } = data || getMockData();
  const greetingTime = new Date().getHours() < 12 ? t('dashboard.greeting_morning') : new Date().getHours() < 17 ? t('dashboard.greeting_afternoon') : t('dashboard.greeting_evening');

  return (
    <AppShell revisionCount={stats.revisionsDueToday} streak={stats.currentStreak}>

      {/* Topbar */}
      <div className="topbar">
        <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text)' }}>
          {greetingTime}, <span style={{ color: 'var(--brand-primary)' }}>{user?.name?.split(' ')[0]}!</span> 👋 Ready to study today?
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '20px', padding: '5px 12px', fontSize: '12px', color: 'var(--muted)', width: '180px', cursor: 'pointer' }} onClick={() => navigate('/subjects')}>
            🔍 &nbsp;{t('dashboard.searchTopics')}
          </div>
        </div>
      </div>

      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

        {/* Announcements banner */}
        {data?.announcements?.length > 0 && (
          <div className="fade-up-1" style={{ background: 'var(--brand-secondary-light)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: '12px', color: 'var(--brand-secondary)', fontWeight: 700 }}>
            📢 {data.announcements[0].title}: {data.announcements[0].message}
          </div>
        )}

        {/* Banner Slot A — only shows if admin uploaded creative */}
        <BannerSlot slot="A" />

        {/* Stats */}
        <div className="fade-up-1" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', gridAutoRows: '1fr' }}>
          <StatCard icon="🎯" value={stats.topicsMastered} label="Topics Mastered" color="var(--brand-primary-light)" />
          <StatCard icon="📅" value={stats.revisionsDueToday} label="Revisions Due Today" color="var(--brand-secondary-light)" trend={stats.revisionsDueToday > 0 ? { up: false, text: t('dashboard.actionNeeded') } : null} />
          <StatCard icon="✅" value={`${stats.avgMasteryScore}%`} label="Avg Mastery Score" color="var(--green-light)" />
          <StatCard icon="🔥" value={`${stats.currentStreak} Days`} label="Study Streak" color="var(--yellow-light)" />
        </div>

        {/* Main grid */}
        <div className="fade-up-2 dashboard-main-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '16px', flexWrap: 'wrap' }}>

          {/* Revisions due */}
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: 800 }}>{`📅 ${t('dashboard.todaysRevisions')}`}</div>
              <span style={{ fontSize: '11px', color: 'var(--brand-primary)', fontWeight: 700, cursor: 'pointer' }} onClick={() => navigate('/revisions')}>View all ›</span>
            </div>

            {revisionsToday.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🎉</div>
                <div className="empty-state-text">{t('dashboard.allCaughtUp')}</div>
                <div className="empty-state-sub">{t('dashboard.noRevisions')}</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                {revisionsToday.map(r => (
                  <RevisionItem key={r.id} revision={r} onRevise={(topicId) => navigate(`/study/${topicId}`)} />
                ))}
              </div>
            )}
          </div>

          {/* Right column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>

            {/* Daily Goal */}
            <div style={{ background: 'var(--brand-secondary)', borderRadius: 'var(--radius)', padding: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 800, color: 'white' }}>🎯 Today's Goal</div>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>Set by you · Reset daily &nbsp;<GoalSetter current={goalTarget} onUpdated={(t) => { setGoalTarget(t); setData(d => ({ ...d, weeklyGoal: { ...d.weeklyGoal, target: t } })); }} /></div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--brand-primary)', lineHeight: 1 }}>
                    {weeklyGoal.completed} <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>/ {weeklyGoal.target}</span>
                  </div>
                  <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.35)' }}>topics</div>
                </div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '10px', height: '6px', marginBottom: '9px' }}>
                <div style={{ background: 'var(--brand-primary)', borderRadius: '10px', height: '100%', width: `${weeklyGoal.target > 0 ? (weeklyGoal.completed / weeklyGoal.target) * 100 : 0}%`, transition: 'width 0.4s' }}></div>
              </div>
              {weeklyGoal.topics?.length > 0 && (
                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                  {weeklyGoal.topics.map((t, i) => (
                    <span key={i} style={{ fontSize: '9px', fontWeight: 700, padding: '2px 7px', borderRadius: '20px', background: t.done ? 'var(--brand-primary)' : 'rgba(255,255,255,0.1)', color: t.done ? 'white' : 'rgba(255,255,255,0.5)' }}>
                      {t.name}{t.done ? ' ✓' : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Continue studying */}
            {continueTopic ? (
              <div
                style={{ background: 'var(--brand-secondary-light)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px', cursor: 'pointer' }}
                onClick={() => navigate(`/study/${continueTopic.topic_id}`)}
              >
                <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--brand-primary)', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '5px' }}>▶ Continue where you left off</div>
                <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--text)', marginBottom: '2px' }}>{continueTopic.topic_name}</div>
                <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '10px' }}>{continueTopic.subject} · {continueTopic.chapter_name}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--muted)', marginBottom: '3px', fontWeight: 600 }}>
                      <span>Chapter progress</span><span>{continueTopic.chapterProgress}%</span>
                    </div>
                    <div style={{ background: 'var(--border)', borderRadius: '10px', height: '5px' }}>
                      <div style={{ background: 'var(--brand-primary)', borderRadius: '10px', height: '100%', width: `${continueTopic.chapterProgress}%` }}></div>
                    </div>
                  </div>
                  <button style={{ background: 'var(--brand-secondary)', color: 'white', border: 'none', borderRadius: '8px', padding: '6px 12px', fontSize: '11px', fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    Continue ›
                  </button>
                </div>
              </div>
            ) : (
              <div
                style={{ background: 'var(--brand-primary-light)', border: '1.5px dashed var(--brand-primary)', borderRadius: 'var(--radius)', padding: '16px', cursor: 'pointer', textAlign: 'center' }}
                onClick={() => navigate('/subjects')}
              >
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>📚</div>
                <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--brand-primary)', marginBottom: '4px' }}>Start Studying!</div>
                <div style={{ fontSize: '11px', color: 'var(--muted)' }}>Pick a subject and begin your first topic</div>
              </div>
            )}

            {/* Weak Topics */}
            {weakTopics?.length > 0 && (
              <div className="card" style={{ padding: '13px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 800 }}>{`📊 ${t('dashboard.weakTopicRadar')}`}</div>
                  <span style={{ fontSize: '11px', color: 'var(--brand-primary)', fontWeight: 700, cursor: 'pointer' }}>Focus ›</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {weakTopics.slice(0, 3).map((t, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 9px', borderRadius: 'var(--radius-sm)', background: 'var(--bg)' }}>
                      <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'var(--red-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 800, color: 'var(--red)', flexShrink: 0 }}>{t.score}%</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.topic_name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--muted)' }}>{t.subject}</div>
                      </div>
                      <div style={{ width: '50px', background: 'var(--border)', borderRadius: '10px', height: '4px', flexShrink: 0 }}>
                        <div style={{ background: 'var(--red)', borderRadius: '10px', height: '100%', width: `${t.score}%` }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Subjects strip */}
        {subjects?.length > 0 && (
          <div className="card fade-up-3">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: 800 }}>{`📚 ${t('dashboard.mySubjects')}`}</div>
              <span style={{ fontSize: '11px', color: 'var(--brand-primary)', fontWeight: 700, cursor: 'pointer' }} onClick={() => navigate('/subjects')}>View all ›</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${subjects.length}, 1fr)`, gap: '9px' }}>
              {subjects.map(s => (
                <div
                  key={s.id}
                  style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '10px', padding: '11px 7px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px', cursor: 'pointer', textAlign: 'center', transition: 'border-color 0.15s' }}
                  onClick={() => navigate(`/subjects/${s.id}`)}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--brand-primary)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <div style={{ fontSize: '20px' }}>{s.icon}</div>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text)' }}>{s.name}</div>
                  <div style={{ width: '100%', background: 'var(--border)', borderRadius: '10px', height: '4px' }}>
                    <div style={{ background: 'var(--brand-primary)', borderRadius: '10px', height: '100%', width: `${s.mastery}%` }}></div>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 600 }}>{s.mastery}/100</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state when brand new */}
        {subjects?.length === 0 && !loading && (
          <div className="card fade-up-3" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🙏</div>
            <h3 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '8px' }}>Welcome to MyPersonalGuruji!</h3>
            <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '24px', maxWidth: '400px', margin: '0 auto 24px' }}>
              Your Guruji is ready. Head to your subjects to start studying your first topic.
            </p>
            <button className="btn-primary" onClick={() => navigate('/subjects')}>
              📚 Go to My Subjects
            </button>
          </div>
        )}

      </div>
    </AppShell>
  );
};

export default DashboardPage;
